import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { useYupValidationResolver } from "@ryde/redux";
import {
  IApiError,
  IDropdownItem,
  IDropdownItemStations,
  isTranslatableItem,
  Log,
  normalizeErrors,
} from "@ryde/shared";
import {
  apiDataFetchingLandingAtom,
  createMessage,
  IWestNegevChooseStationLandingModel,
  landingDropdownStationListAtom,
  landingMessagesAtom,
  LandingStepEnum,
  profileLandingDtoAtom,
  useLandingActions,
  useLandingApiSites,
  useLandingApiStations,
  useLandingApiUnits,
  useLandingsAuth,
  WESTNEGEV_AGE_GROUPS,
  WestNegevChooseStationStepFormFieldsEnum,
} from "@ryde/utils-landings";

import { useYupChooseStationLandingValidationSchema } from "./parts";

export const useStepChooseStationWestNegev = () => {
  const [searchParams] = useSearchParams();
  const phoneFromParams = searchParams.get("phone") || "";
  const { goToStep } = useLandingActions();
  const { getOfficialStationsByHomeAddress, setPassengerPickupStation } =
    useLandingApiStations();
  const { getSitesDropdownItems } = useLandingApiSites();
  const { getUnitsDropdownItems, getStationsDropdownItems } =
    useLandingApiUnits();
  const setStationsDropdownList = useSetRecoilState(
    landingDropdownStationListAtom,
  );
  const { yupChooseStationLandingValidationSchema } =
    useYupChooseStationLandingValidationSchema();
  const { t } = useTranslation();

  const setServerErrors = useSetRecoilState(landingMessagesAtom);
  const setApiLoading = useSetRecoilState(apiDataFetchingLandingAtom);
  const { handleLogout } = useLandingsAuth();
  const profile = useRecoilValue(profileLandingDtoAtom);
  const [sitesDropdownList, setSitesDropdownList] = useState<IDropdownItem[]>(
    [],
  );
  const [unitsDropdownList, setUnitsDropdownList] = useState<IDropdownItem[]>(
    [],
  );
  const [ageDropdownList] = useState<IDropdownItem[]>(
    WESTNEGEV_AGE_GROUPS.map(group => ({ value: group, label: group })),
  );
  const [preferredStationsDropdownList, setPreferredStationsDropdownList] =
    useState<IDropdownItem[]>([]);
  const [
    preferredDestinationStationsList,
    setPreferredDestinationStationsList,
  ] = useState<IDropdownItem[]>([]);

  const resolver = useYupValidationResolver(
    yupChooseStationLandingValidationSchema,
    "landing",
  );

  const methods = useForm<IWestNegevChooseStationLandingModel>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      siteId: undefined,
      unitId: undefined,
      age: undefined,
      firstName: "",
      lastName: "",
      phoneMobile: phoneFromParams,
      preferredStationId: undefined,
      homeAddress: {
        fullAddress: "",
        longitude: undefined,
        latitude: undefined,
      },
      sourceStation2Id: undefined,
      destinationId: undefined,
      acceptTerms: false,
      acceptPrivacyPolicy: false,
    },
    resolver,
  });

  const {
    getValues,
    clearErrors,
    setError,
    setValue,
    trigger,
    watch,
    formState: { isValid },
  } = methods;

  const homeAddressWatchValue = watch(
    WestNegevChooseStationStepFormFieldsEnum.HomeAddress,
  );
  const sourceStation2IdWatchValue = watch(
    WestNegevChooseStationStepFormFieldsEnum.SourceStation2Id,
  );
  const destinationIdWatchValue = watch(
    WestNegevChooseStationStepFormFieldsEnum.DestinationId,
  );
  const preferredStationIdWatchValue = watch(
    WestNegevChooseStationStepFormFieldsEnum.PreferredStationId,
  );
  const phoneMobileWatchValue = watch(
    WestNegevChooseStationStepFormFieldsEnum.PhoneMobile,
  );

  // Fetch sites on component mount
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await getSitesDropdownItems();
        if (response?.success && response.data) {
          setSitesDropdownList(response.data);
        }
      } catch (error) {
        Log.error("Failed to fetch sites:", error);
      }
    };

    fetchSites();
  }, [getSitesDropdownItems]);

  // Fetch units on component mount
  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const response = await getUnitsDropdownItems();
        if (response?.success && response.data) {
          setUnitsDropdownList(response.data);
        }
      } catch (error) {
        Log.error("Failed to fetch units:", error);
      }
    };

    fetchUnits();
  }, [getUnitsDropdownItems]);

  // Fetch preferred stations on component mount
  useEffect(() => {
    const fetchPreferredStations = async () => {
      try {
        const response = await getStationsDropdownItems({ isOfficial: true });
        if (response?.success && response.data) {
          const sortedData = response.data.sort((a, b) =>
            (a.label || "").localeCompare(b.label || ""),
          );
          setPreferredStationsDropdownList(sortedData);
        }
      } catch (error) {
        Log.error("Failed to fetch preferred stations:", error);
      }
    };

    fetchPreferredStations();
  }, [getStationsDropdownItems]);

  // Fetch preferred destination stations on component mount
  useEffect(() => {
    const fetchPreferredDestinationStations = async () => {
      try {
        const response = await getStationsDropdownItems({ isOfficial: true });
        if (response?.success && response.data) {
          const sortedData = response.data.sort((a, b) =>
            (a.label || "").localeCompare(b.label || ""),
          );
          setPreferredDestinationStationsList(sortedData);
        }
      } catch (error) {
        Log.error("Failed to fetch preferred destination stations:", error);
      }
    };

    fetchPreferredDestinationStations();
  }, [getStationsDropdownItems]);

  // Pre-fill phone from profile
  useEffect(() => {
    if (profile?.phone && !phoneMobileWatchValue) {
      setValue(
        WestNegevChooseStationStepFormFieldsEnum.PhoneMobile,
        profile.phone,
      );
    }
  }, [profile, phoneMobileWatchValue, setValue]);

  const handleHomeAddressChange = useEvent(async (): Promise<void> => {
    setServerErrors(undefined);
    const homeAddress = getValues(
      WestNegevChooseStationStepFormFieldsEnum.HomeAddress,
    );

    if (!homeAddress.latitude || !homeAddress.longitude) {
      setError(WestNegevChooseStationStepFormFieldsEnum.HomeAddress, {
        message: t("errors.missingCoordinates"),
      });
      setValue(
        WestNegevChooseStationStepFormFieldsEnum.SourceStation2Id,
        undefined,
      );
      setStationsDropdownList([]);
      return;
    }

    try {
      const response = await getOfficialStationsByHomeAddress({
        homeAddress,
      });

      const { success, data } = response ?? {};

      if (success && data) {
        if (
          "isHomeAddressInPermittedPolygon" in data &&
          !data.isHomeAddressInPermittedPolygon
        ) {
          setError(WestNegevChooseStationStepFormFieldsEnum.HomeAddress, {
            message: t("errors.notInPermittedPolygon"),
          });
        }

        if (data.officialStationsList?.length) {
          setStationsDropdownList(
            data.officialStationsList as IDropdownItemStations[],
          );
        }

        setValue(
          WestNegevChooseStationStepFormFieldsEnum.SourceStation2Id,
          undefined,
        );
        clearErrors(WestNegevChooseStationStepFormFieldsEnum.SourceStation2Id);

        if (data.selectedStationId) {
          setValue(
            WestNegevChooseStationStepFormFieldsEnum.SourceStation2Id,
            data.selectedStationId,
            {
              shouldDirty: true,
              shouldValidate: true,
            },
          );
        } else {
          setError(WestNegevChooseStationStepFormFieldsEnum.SourceStation2Id, {
            message: t("errors.noNearbyStationFoundMessage"),
          });
        }
      }
    } catch (error) {
      const errorsNormalized = normalizeErrors(error as IApiError, t);
      setServerErrors(createMessage(errorsNormalized));
      Log.warnAndToast(errorsNormalized, error);
    }
  });

  /**
   * The consent checkboxes are the last thing the page asks for, and they are the only fields the schema
   * requires that no template writes: without `shouldValidate` the value lands but `isValid` keeps the
   * stale `false` it held while the boxes were unticked, leaving the continue button dead until some
   * other field revalidates the form.
   */
  const handleConsentToggle = useEvent(
    (
      field:
        | WestNegevChooseStationStepFormFieldsEnum.AcceptTerms
        | WestNegevChooseStationStepFormFieldsEnum.AcceptPrivacyPolicy,
    ): void => {
      setValue(field, !getValues(field), {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
  );

  const handleChooseStation = useEvent(async (): Promise<void> => {
    const isValidAfterTrigger = await trigger();

    if (!isValidAfterTrigger) {
      return;
    }

    const siteId = getValues(WestNegevChooseStationStepFormFieldsEnum.SiteId);
    const unitId = getValues(WestNegevChooseStationStepFormFieldsEnum.UnitId);
    const preferredStationId = getValues(
      WestNegevChooseStationStepFormFieldsEnum.PreferredStationId,
    );
    const age = getValues(WestNegevChooseStationStepFormFieldsEnum.Age);
    const firstName = getValues(
      WestNegevChooseStationStepFormFieldsEnum.FirstName,
    );
    const lastName = getValues(
      WestNegevChooseStationStepFormFieldsEnum.LastName,
    );
    const phoneMobile = getValues(
      WestNegevChooseStationStepFormFieldsEnum.PhoneMobile,
    );
    const destinationId = getValues(
      WestNegevChooseStationStepFormFieldsEnum.DestinationId,
    );

    if (preferredStationId && destinationId) {
      setApiLoading(true);

      try {
        const acceptTerms = getValues(
          WestNegevChooseStationStepFormFieldsEnum.AcceptTerms,
        );
        const acceptPrivacyPolicy = getValues(
          WestNegevChooseStationStepFormFieldsEnum.AcceptPrivacyPolicy,
        );
        const response = await setPassengerPickupStation({
          sourceStation2Id: preferredStationId,
          age: age,
          destinationStationId: destinationId,
          firstName,
          lastName,
          phone: phoneMobile,
          siteId,
          unitId,
          acceptTerms,
          acceptPrivacyPolicy,
        });

        const { success, data, error } = response ?? {};

        if (success && data) {
          setServerErrors(createMessage([]));
          goToStep(LandingStepEnum.FinishedStep);
          handleLogout();
          return;
        }

        if (!success && error) {
          const errorMessage = isTranslatableItem(error)
            ? error.translationKey
            : "errors.updateNotSuccess";
          setServerErrors(createMessage([errorMessage]));
          setApiLoading(false);
          return;
        }
      } catch (error) {
        Log.error(
          "failed update passenger homeStation and sourceStation2Id. error:",
          error,
        );
      } finally {
        setApiLoading(false);
      }

      setServerErrors(createMessage(["errors.updateNotSuccess"]));
    }
  });

  const isContinueCanBeApply =
    isValid && preferredStationIdWatchValue && destinationIdWatchValue;

  return {
    methods,
    handleChooseStation,
    handleConsentToggle,
    handleHomeAddressChange,
    isContinueCanBeApply,
    sitesDropdownList,
    unitsDropdownList,
    ageDropdownList,
    preferredStationsDropdownList,
    preferredDestinationStationsList,
  };
};
